import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Phone, Clock, Mail, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXTERIOR_IMAGE = "https://customer-assets.emergentagent.com/job_bcf6ecb8-480f-48a0-b555-9c0d6414b56f/artifacts/5nv0ba1o_Capture%20d%27%C3%A9cran%202026-02-19%20172943.png";

const Contact = () => {
  useSEO({
    title: 'Contact & Plan d\'accès — NotaBene Sympho (Mons)',
    description: 'Adresse, téléphone, horaires et plan Google Maps du restaurant NotaBene Sympho. Chaussée du Roi Baudouin 161, 7030 Mons.',
    canonical: '/contact',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });

      setSuccess(true);
      toast.success('Message envoyé !');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F5F2EA]" data-testid="contact-page">
      {/* Header */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
            Contact
          </p>
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold text-[#1C1917] mb-6">
            Nous contacter
          </h1>
          <p className="text-[#57534E] text-lg max-w-2xl mx-auto">
            Une question, une demande spéciale ou simplement envie de nous dire bonjour ? 
            N'hésitez pas à nous contacter !
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg mb-8">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1C1917] mb-8">
                  Informations
                </h2>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#C62E2E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917] mb-1">Adresse</h3>
                      <p className="text-[#57534E]">
                        Chaussée du Roi Baudouin 161<br />
                        7030 Mons, Belgique
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#C62E2E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917] mb-1">Téléphone</h3>
                      <a href="tel:+3265950649" className="text-[#57534E] hover:text-[#C62E2E] transition-colors">
                        065 95 06 49
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#C62E2E]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#C62E2E]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1917] mb-1">Horaires</h3>
                      <ul className="text-[#57534E] space-y-1">
                        <li className="flex justify-between gap-8">
                          <span>Lundi - Mardi</span>
                          <span className="text-[#C62E2E]">Fermé</span>
                        </li>
                        <li className="flex justify-between gap-8">
                          <span>Mercredi - Dimanche</span>
                          <span>12h-14h30 / 18h-22h</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Restaurant Image */}
              <img
                src={EXTERIOR_IMAGE}
                alt="NotaBene Sympho - Extérieur"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
                {success ? (
                  <div className="text-center py-8" data-testid="contact-success">
                    <div className="w-20 h-20 rounded-full bg-[#3A6351]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-[#3A6351]" />
                    </div>
                    <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1C1917] mb-4">
                      Message envoyé !
                    </h2>
                    <p className="text-[#57534E] mb-4">
                      Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
                    </p>
                    <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 mb-6 text-left">
                      <p className="text-[#78350F] text-sm font-semibold mb-1">
                        📧 Vérifiez vos emails (et vos spams)
                      </p>
                      <p className="text-[#78350F] text-sm">
                        Notre réponse arrivera <strong>par email</strong>. Pensez à consulter votre boîte de réception <strong>absolument</strong>.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setSuccess(false);
                        setFormData({ name: '', email: '', message: '' });
                      }}
                      variant="outline"
                      className="rounded-full"
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1C1917] mb-8">
                      Envoyez-nous un message
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <Label htmlFor="contact-name" className="mb-2 block">
                          Nom complet *
                        </Label>
                        <Input
                          id="contact-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Votre nom"
                          className="rounded-lg"
                          data-testid="contact-name"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact-email" className="flex items-center gap-2 mb-2">
                          <Mail className="w-4 h-4" />
                          Email *
                        </Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Votre email"
                          className="rounded-lg"
                          data-testid="contact-email"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="contact-message" className="mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          id="contact-message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Votre message..."
                          className="rounded-lg"
                          rows={5}
                          data-testid="contact-message"
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full py-6 text-lg font-semibold"
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white"></div>
                            Envoi en cours...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Envoyer le message
                          </span>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1C1917] mb-4">
              Comment nous trouver
            </h2>
            <p className="text-[#57534E]">
              Situé sur la Chaussée du Roi Baudouin, facile d'accès avec parking disponible.
            </p>
          </div>
          
          <div className="map-container aspect-[16/9] md:aspect-[21/9]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2541.759893234567!2d3.9444444!3d50.4444444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c24e6d83d5e6a9%3A0x1234567890abcdef!2sChaussee%20du%20Roi%20Baudouin%20161%2C%207030%20Mons%2C%20Belgium!5e0!3m2!1sen!2sbe!4v1234567890123!5m2!1sen!2sbe"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="NotaBene Sympho Location"
            />
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-[#1C1917]" data-testid="quick-actions-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href="tel:+3265950649"
              className="bg-white/10 hover:bg-white/20 rounded-2xl p-6 text-center transition-colors"
              data-testid="quick-call"
            >
              <Phone className="w-8 h-8 text-white mx-auto mb-3" />
              <p className="text-white font-semibold">Appeler</p>
              <p className="text-white/70 text-sm">065 95 06 49</p>
            </a>
            
            <a
              href="https://maps.google.com/?q=Chaussée+du+Roi+Baudouin+161+7030+Mons+Belgium"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-white/20 rounded-2xl p-6 text-center transition-colors"
              data-testid="quick-directions"
            >
              <MapPin className="w-8 h-8 text-white mx-auto mb-3" />
              <p className="text-white font-semibold">Itinéraire</p>
              <p className="text-white/70 text-sm">Google Maps</p>
            </a>
            
            <a
              href="/reservation"
              className="bg-[#C62E2E] hover:bg-[#a02424] rounded-2xl p-6 text-center transition-colors"
              data-testid="quick-reserve"
            >
              <Clock className="w-8 h-8 text-white mx-auto mb-3" />
              <p className="text-white font-semibold">Réserver</p>
              <p className="text-white/70 text-sm">Une table</p>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
